import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ELocale } from '~~/types/enums'
import { ResponseCode } from '~/utils/constant'
import { handleNewsUrl } from '~/utils/common'
import _ from 'lodash'

export const useWikiList = async (secondIndustryId: string, locale: ELocale) => {
  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()
  useLang(locale)

  // Reactive data
  const industry = ref<any[]>([])
  const fair = ref({
    total: 0,
    records: [] as any[]
  })
  const secondIndustryName = ref('')

  // Computed
  const title = ref('')
  const langApi = ref(locale === ELocale.en ? 'en' : 'cn')
  const pageNum = ref(1)
  const page = route.params.page as string
  pageNum.value = isNaN(+page) ? 1 : +page
  const pageSize = 12

  // Methods
  const fetchData = async () => {
    try {
      const { useHttp } = await import('~/composables/useHttp')

      const params: { locale: string; pageNum: number; pageSize: number; industryId?: string } = {
        locale: langApi.value,
        pageNum: pageNum.value,
        pageSize
      }

      if (+secondIndustryId > 0) {
        params['industryId'] = secondIndustryId
      }

      const requestList: any[] = [
        useHttp.get(`/api/industry/getAll?locale=${langApi.value}`),
        useHttp.post('/api/wiki/list', params),
        useHttp.post('/api/newsApi/homePageList', {
          locale: langApi.value,
          pageNum: pageNum.value,
          pageSize
        })
      ]

      const result: any[] = await Promise.all(requestList)
      const [industryRes, fairRes, wikiRes] = result

      // 处理行业数据
      if (industryRes.code === ResponseCode.SUCCESS) {
        let list: any = industryRes.data
        industry.value = industryRes.data

        // 查询行业名称
        for (let i = 0; i < list.length; i++) {
          let secondList = list[i].children
          for (let x = 0; x < secondList.length; x++) {
            if (secondList[x].id == secondIndustryId) {
              secondIndustryName.value = secondList[x].industryName
              break
            }
          }
        }
        title.value = `${secondIndustryName.value}展会百科`
      }

      // 处理展会数据
      if (fairRes?.code === ResponseCode.SUCCESS) {
        let list = fairRes.data.records

        list.map((item: any) => {
          item['cover'] = ''
          if (item.logo) {
            let arr: any = JSON.parse(item.logo)
            item['cover'] = arr.length > 0 ? arr[0] : ''
          }
          item['description'] = item.profile
            ? item.profile.replace(/<[^>]*>/g, '').slice(0, 100) + '...'
            : ''
        })

        fair.value.total = fairRes.data.total
        fair.value.records = list

        if (pageNum.value > 1 && pageNum.value > fairRes.data.pages) {
          throw createError({
            statusCode: 404,
            statusMessage: 'Page not found'
          })
        }
      }

      // 处理百科数据
      if (wikiRes?.code === ResponseCode.SUCCESS && wikiRes.data && wikiRes.data.records) {
        if (pageNum.value > 1 && pageNum.value > wikiRes.data.pages) {
          throw createError({
            statusCode: 404,
            statusMessage: 'Page not found'
          })
        }
      }
    } catch (error) {
      console.error('Error fetching wiki list data:', error)
      throw error
    }
  }

  const changePager = (page: number) => {
    router.push({
      path: `/wiki-${secondIndustryId}-${page}/`,
      query: route.query
    })
  }

  const formatIndustry = (val: string): string => {
    if (!val) return ''
    const vals = val.split('/')
    return vals.slice(0, 2).join('/')
  }

  const subIndustry = (idx: number) => {
    const index = idx % 8
    switch (index) {
      case 0:
        return 'transform: translateX(-8%);'
      case 1:
        return 'transform: translateX(-16%);'
      case 2:
        return 'transform: translateX(-42%);'
      case 3:
        return 'transform: translateX(-22%);'
      case 4:
        return 'transform: translateX(-58%);'
      case 5:
        return 'transform: translateX(-58%);'
      case 6:
        return 'transform: translateX(-58%);'
      case 7:
        return 'transform: translateX(-58%);'
    }
  }

  const formatTime = (val: string) => {
    return new Date(val).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const description = (item: Record<string, any>): any => {
    if (item.description) {
      return item.description
    } else {
      const reg = /<[^>]+>/gi
      const description =
        (item.content ?? '') != ''
          ? item.content
              ?.replace(reg, '')
              ?.replace(/&nbsp;/g, '')
              .trim()
          : ''
      return description ? description?.slice(0, 100) + '...' : ''
    }
  }

  const formatLogo = (value: string): string => {
    let parse: any = value
    if (typeof value === 'string') {
      try {
        parse = JSON.parse(value)[0]
          .replace('http://', 'https://')
          .replace('http://w3-file.jufair.com', 'https://w3-file.jufair.com')
      } catch (error) {
        parse = value
        console.error(error)
      }
    }
    return parse + '!logo'
  }

  return {
    industry,
    fair,
    secondIndustryName,
    title,
    langApi,
    pageNum,
    pageSize,
    fetchData,
    changePager,
    formatIndustry,
    subIndustry,
    formatTime,
    description,
    formatLogo,
    handleNewsUrl
  }
}
